import React, { useLayoutEffect, useRef } from "react"
import {
  AppBar,
  Container,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
  DialogActions,
  Skeleton,
  Box
} from "@mui/material"
import { FaIcon } from "../../../../mp-core/icons"
import { useStyles } from "./styles"
import { IconSize } from "../../../../mp-core/icons/types"

type Props = {
  open: boolean
  inactive?: boolean
  onClose?: () => void
  onEntered?: () => void
  closeIcon?: {
    name: string
    className?: string
    size?: IconSize
  }
  title: string
  actions: React.ReactNode
  loading?: boolean
  dataAutomationId?: string
  disableContentPadding?: boolean
  whiteBackground?: boolean
  outerClasses?: {
    paper?: string
    content?: string
    footer?: string
  }
}

// a11y: keep disableEnforceFocus (below) so ChromeVox can Search+Arrow into the
// dialog body, and trap the keyboard by making the app background inert instead.
// inert is passive (won't fight the AT cursor) and drops #root from tab order +
// the a11y tree. Ref-counted so stacked modals don't clear it early.
let openFullscreenModalCount = 0

const setAppBackgroundInert = (inert: boolean) => {
  const appRoot = document.getElementById("root")
  if (!appRoot) return
  openFullscreenModalCount = Math.max(
    0,
    openFullscreenModalCount + (inert ? 1 : -1)
  )
  if (openFullscreenModalCount > 0) {
    appRoot.setAttribute("inert", "")
  } else {
    appRoot.removeAttribute("inert")
  }
}

const FullscreenModal: React.FC<React.PropsWithChildren<Props>> = ({
  open,
  inactive = false,
  onClose,
  onEntered,
  closeIcon = { name: "arrow-left" },
  title,
  children,
  actions,
  loading = false,
  dataAutomationId,
  disableContentPadding = false,
  whiteBackground = false,
  outerClasses
}) => {
  const { classes, cx } = useStyles({ disableContentPadding, whiteBackground })
  const triggerRef = useRef<HTMLElement | null>(null)

  // a11y: while open, make the app background (#root) inert (see
  // setAppBackgroundInert). We restore focus to the opener ourselves (with
  // disableRestoreFocus below): inerting the trigger's ancestor blurs it before
  // MUI records its restore target, so MUI's own restore would land on <body>.
  useLayoutEffect(() => {
    if (!open) return undefined
    triggerRef.current = document.activeElement as HTMLElement | null
    setAppBackgroundInert(true)
    return () => {
      setAppBackgroundInert(false)
      const trigger = triggerRef.current
      if (trigger && document.contains(trigger)) trigger.focus()
      triggerRef.current = null
    }
  }, [open])

  return (
    <Dialog
      fullScreen
      open={open}
      sx={inactive ? { visibility: "hidden" } : undefined}
      onClose={onClose}
      disableEnforceFocus
      disableRestoreFocus
      classes={{ paper: cx(classes.paper, outerClasses?.paper) }}
      data-automation-id={dataAutomationId}
      aria-labelledby={`${dataAutomationId || "fullscreen-modal"}-title`}
      TransitionProps={{ onEntered }}
    >
      <AppBar className={classes.header}>
        <Toolbar className={classes.toolbar}>
          {Boolean(onClose) && (
            <IconButton
              onClick={onClose}
              aria-label="Close"
              className={classes.backButton}
              data-automation-id={
                dataAutomationId ? `${dataAutomationId}-back-button` : undefined
              }
            >
              <FaIcon
                solid
                name={closeIcon.name}
                className={cx(classes.backButtonIcon, closeIcon.className)}
                {...(closeIcon.size ? { size: closeIcon.size } : {})}
              />
            </IconButton>
          )}
          {loading && !title ? (
            <Skeleton variant="rectangular" className={classes.titleSkeleton} />
          ) : (
            <Typography
              id={`${dataAutomationId || "fullscreen-modal"}-title`}
              variant="h4"
              component="h1"
              className={classes.title}
              data-automation-id={
                dataAutomationId ? `${dataAutomationId}-title` : undefined
              }
            >
              {title}
            </Typography>
          )}
        </Toolbar>
      </AppBar>

      {disableContentPadding ? (
        <Box className={cx(classes.content, outerClasses?.content)}>
          {children}
        </Box>
      ) : (
        <Container className={cx(classes.content, outerClasses?.content)}>
          {children}
        </Container>
      )}

      <DialogActions className={cx(classes.footer, outerClasses?.footer)}>
        {actions}
      </DialogActions>
    </Dialog>
  )
}

export default FullscreenModal
